import { lazy, Suspense, useEffect, useRef } from 'react'
import { FluidHeading } from '../components/FluidHeading'
import { SmoothAnchor } from '../components/SmoothAnchor'
import { Reveal } from '../components/Reveal'

const Scene = lazy(() => import('../three/Scene').then((m) => ({ default: m.Scene })))

export function Hero() {
  const scrollProgress = useRef(0)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onScroll() {
      const vh = window.innerHeight
      scrollProgress.current = Math.min(1, Math.max(0, window.scrollY / (vh * 1.1)))
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-5 pt-28 text-center"
    >
      <div className="absolute inset-0">
        <Suspense fallback={null}>
          <Scene scrollProgress={scrollProgress} />
        </Suspense>
      </div>

      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 68% 64% at 50% 46%, rgba(5,3,15,0.92) 0%, rgba(5,3,15,0.7) 45%, transparent 76%)',
        }}
      />

      <div className="relative z-10 flex flex-col items-center">
        <Reveal delay={0} y={16}>
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-orb-400/25 bg-space-950/60 px-4 py-1.5 text-xs font-medium tracking-wide text-orb-200 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-pulse-slow rounded-full bg-orb-300" />
            Estratégia, tráfego e performance digital
          </span>
        </Reveal>

        <h1 className="max-w-3xl text-balance text-4xl font-semibold leading-[1.15] text-white [text-shadow:0_2px_30px_rgba(5,3,15,0.9)] sm:max-w-4xl sm:text-6xl sm:leading-[1.08] md:text-7xl">
          <FluidHeading as="span" className="block">
            Tudo gira em torno do
          </FluidHeading>
          <FluidHeading as="span" className="mt-1 block sm:mt-2">
            <span className="text-gradient">resultado.</span>
          </FluidHeading>
        </h1>

        <Reveal delay={0.55} y={20}>
          <p className="mt-6 max-w-xl text-balance text-base leading-relaxed text-white/70 [text-shadow:0_2px_16px_rgba(5,3,15,0.95)] sm:text-lg">
            A Orb conecta estratégia, tráfego pago, criação e presença digital
            para transformar investimento em crescimento.
          </p>
        </Reveal>

        <Reveal delay={0.8} y={20}>
          <div className="mt-9 flex flex-col items-center gap-4 sm:flex-row">
            <SmoothAnchor
              href="#contato"
              className="w-full rounded-full bg-gradient-to-r from-orb-400 to-orb-700 px-8 py-4 text-sm font-semibold text-white shadow-[0_0_40px_-6px_rgba(139,60,240,1)] transition-transform hover:scale-105 sm:w-auto sm:px-9"
            >
              Quero gerar mais resultados
            </SmoothAnchor>
            <SmoothAnchor
              href="#servicos"
              className="w-full rounded-full border border-white/12 px-8 py-3.5 text-sm font-medium text-white/65 transition-colors hover:border-white/25 hover:text-white sm:w-auto"
            >
              Conheça nossas soluções
            </SmoothAnchor>
          </div>
        </Reveal>
      </div>

      <Reveal delay={1.2} y={10} className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 sm:block">
        <div className="flex flex-col items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/35">Scroll</span>
          <div className="h-9 w-5 rounded-full border border-white/20 p-1">
            <div className="h-1.5 w-full animate-float rounded-full bg-orb-300" />
          </div>
        </div>
      </Reveal>
    </section>
  )
}
